import { Controller } from "@hotwired/stimulus";

const SVG_NS = "http://www.w3.org/2000/svg";
const SERIES = [
  { key: "label", label: "标注", color: "--color-primary" },
  { key: "check", label: "质检", color: "--color-secondary" },
  { key: "accept", label: "验收", color: "--color-accent" },
];

export default class extends Controller {
  static targets = ["trend"];
  static values = { trend: Array };

  connect() {
    this.render = this.render.bind(this);
    this.render();

    this.themeObserver = new MutationObserver(this.render);
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.mediaQuery.addEventListener("change", this.render);
  }

  disconnect() {
    this.themeObserver?.disconnect();
    this.mediaQuery?.removeEventListener("change", this.render);
  }

  render() {
    if (!this.hasTrendTarget) return;

    const data = this.trendValue;
    const width = 900;
    const height = 300;
    const padding = { top: 24, right: 20, bottom: 42, left: 46 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const maximum = Math.max(1, ...data.flatMap((point) => SERIES.map((series) => Number(point[series.key]) || 0)));
    const gridMaximum = this.roundMaximum(maximum);
    const styles = getComputedStyle(document.documentElement);
    const colors = Object.fromEntries(SERIES.map((series) => [series.key, styles.getPropertyValue(series.color).trim()]));
    const muted = `color-mix(in oklab, ${styles.getPropertyValue("--color-base-content").trim()} 42%, transparent)`;
    const grid = `color-mix(in oklab, ${styles.getPropertyValue("--color-base-content").trim()} 12%, transparent)`;

    this.trendTarget.replaceChildren();
    this.trendTarget.setAttribute("viewBox", `0 0 ${width} ${height}`);
    this.trendTarget.setAttribute("role", "img");
    this.trendTarget.setAttribute("aria-label", "近 14 天标注、质检与验收趋势图");

    for (let index = 0; index <= 4; index += 1) {
      const y = padding.top + (plotHeight / 4) * index;
      const value = Math.round(gridMaximum - (gridMaximum / 4) * index);
      this.trendTarget.append(
        this.svg("line", { x1: padding.left, y1: y, x2: width - padding.right, y2: y, stroke: grid, "stroke-width": 1 }),
        this.svg("text", { x: padding.left - 10, y: y + 4, fill: muted, "font-size": 11, "text-anchor": "end" }, value),
      );
    }

    data.forEach((point, index) => {
      if (index % 2 !== 0 && index !== data.length - 1) return;

      const x = this.xPosition(index, data.length, padding.left, plotWidth);
      this.trendTarget.append(
        this.svg("text", { x, y: height - 14, fill: muted, "font-size": 11, "text-anchor": "middle" }, point.date),
      );
    });

    SERIES.forEach((series) => {
      const points = data.map((point, index) => {
        const x = this.xPosition(index, data.length, padding.left, plotWidth);
        const y = padding.top + plotHeight - ((Number(point[series.key]) || 0) / gridMaximum) * plotHeight;
        return { x, y, value: Number(point[series.key]) || 0, date: point.date };
      });
      const path = points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");

      this.trendTarget.append(
        this.svg("path", {
          d: path,
          fill: "none",
          stroke: colors[series.key],
          "stroke-width": 2.5,
          "stroke-linecap": "round",
          "stroke-linejoin": "round",
        }),
      );

      points.forEach((point) => {
        const circle = this.svg("circle", {
          cx: point.x,
          cy: point.y,
          r: 3.5,
          fill: colors[series.key],
          stroke: styles.getPropertyValue("--color-base-100").trim(),
          "stroke-width": 2,
        });
        circle.append(this.svg("title", {}, `${point.date} ${series.label}：${point.value}`));
        this.trendTarget.append(circle);
      });
    });
  }

  xPosition(index, count, left, width) {
    return count <= 1 ? left + width / 2 : left + (index / (count - 1)) * width;
  }

  roundMaximum(maximum) {
    const magnitude = 10 ** Math.floor(Math.log10(maximum));
    return Math.ceil(maximum / magnitude) * magnitude;
  }

  svg(tag, attributes = {}, text = null) {
    const element = document.createElementNS(SVG_NS, tag);
    Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
    if (text !== null) element.textContent = text;
    return element;
  }
}
